import React from "react";
import axiosConfig from "restApi/axiosConfig";
import { UserContext } from "contexts/UserContext";
import Admin from "./Admin";
import { Box, CircularProgress, Container, Paper } from "@material-ui/core";
import { API_BASE_URL } from "CONSTANTS";

export default () => {
  const [data, setData] = React.useState({
    team: {},
    loading: true,
    errorMessage: "",
  });
  const userContext = React.useContext(UserContext);

  React.useEffect(() => {
    axiosConfig
      .get(`/api/teams/${userContext.user.team}/`)
      .then((res) => {
        setData({
          team: res.data,
          loading: false,
          errorMessage: "",
        });
      })
      .catch((err) => {
        if (err.response) {
          if (err.response.status === 403) {
            setData({
              loading: false,
              errorMessage: "You do not have permission to view this page.",
            });
          } else {
            setData({
              loading: false,
              errorMessage: JSON.stringify(err.response.data),
            });
          }
        }
      });
  }, [userContext.user.team]);

  // The team's invitation link (the current one, restarted for another week;
  // `newLink` replaces it on purpose). The InviteDialog turns it into a message.
  const fetchInvite = (newLink) =>
    axiosConfig
      .get(`api/team/invitation/create${newLink ? "?new=1" : ""}`)
      .then((res) => {
        setData((previous) => ({
          ...previous,
          team: { ...previous.team, invitation_code: res.data.code },
        }));
        return {
          url: `${API_BASE_URL}/signup?invitation=${res.data.code}`,
          expiresAt: res.data.expires_at,
        };
      });

  const connectToSpotify = () => {
    axiosConfig
      .get("/api/spotify/authorize")
      .then((res) => {
        window.location.href = res.data;
      })
      .catch((err) => console.log(err));
  };

  // Returns the request so the confirmation dialog can wait on it and show an
  // error if it fails.
  const disconnectSpotify = () =>
    axiosConfig.post("/api/spotify/deactivate").then(() => {
      setData({ ...data, team: { ...data.team, spotify_username: false } });
    });

  const updateMemberLeaderStatus = (userId, isTeamLeader) =>
    axiosConfig
      .patch(`/api/team/members/${userId}/`, { is_team_leader: isTeamLeader })
      .then((res) => {
        setData({
          ...data,
          team: {
            ...data.team,
            member: data.team.member.map((m) => (m.id === userId ? { ...m, ...res.data } : m)),
          },
        });
        return res.data;
      });

  // Removes only their membership of this team (their account stays) -
  // see UpdateMemberLeaderStatus.delete in api/views.py.
  const removeMember = (userId) =>
    axiosConfig.delete(`/api/team/members/${userId}/`).then(() => {
      setData({
        ...data,
        team: {
          ...data.team,
          member: data.team.member.filter((m) => m.id !== userId),
        },
      });
    });

  // `fields`: { name?, nickname? } - either or both.
  const renameTeam = (fields) =>
    axiosConfig.patch("/api/team/rename/", fields).then((res) => {
      setData({
        ...data,
        team: {
          ...data.team,
          name: res.data.name,
          nickname: res.data.nickname,
          display_name: res.data.display_name,
        },
      });
      return res.data;
    });

  return (
    <Container maxWidth="lg">
      {data.loading ? (
        <Box
          style={{
            position: "absolute",
            top: "30%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
        >
          <CircularProgress color="secondary" />
        </Box>
      ) : data.errorMessage ? (
        <Paper>
          <Box p={3} textAlign="center">
            {data.errorMessage}
          </Box>
        </Paper>
      ) : (
        <Admin
          team={data.team}
          currentUserId={userContext.user.id}
          canManage={!!(userContext.user.is_team_leader || userContext.user.is_team_admin)}
          fetchInvite={fetchInvite}
          connectToSpotify={connectToSpotify}
          disconnectSpotify={disconnectSpotify}
          updateMemberLeaderStatus={updateMemberLeaderStatus}
          removeMember={removeMember}
          renameTeam={renameTeam}
        />
      )}
    </Container>
  );
};